<template>
  <div :class="['text-input', { 'error': localError || error?.length || hasChanges }, customClass]">
    <span class="d-flex gap-10" v-if="errorPlacement == 'top'">
      <small v-if="error && error?.length" class="text-secondary-red sm-fs-xxs"> {{ Array.isArray(error) ? error.join('. ') : error }} </small>
      <small v-if="localError && !error?.length" class="text-secondary-red sm-fs-xxs"> {{ localError }} </small>
    </span>
    <label class="body-text" :for="label">{{ label }}  <span v-if="isRequired" class="text-danger"> * </span></label>
    <div class="input-date-wrapper">
      <input ref="inputRef" :type="currentType" :id="label" :placeholder="placeholder"
        :class="['links', 'date-input', { 'has-icon': icon }]" :maxlength="maxlength" :max="maxDate" :min="minDate"
        :value="inputValue" :readonly="readonly" @input="onInput" />

      <i v-if="icon" class="custom-date-icon iatse-icon-calendar" @click="focusInput"></i>
    </div>
    <span class="d-flex gap-10" v-if="errorPlacement == 'bottom'">
      <small v-if="error && error?.length" class="text-secondary-red sm-fs-xxs"> {{ Array.isArray(error) ? error.join('. ') : error }} </small>
      <small v-if="localError && !error?.length" class="text-secondary-red sm-fs-xxs"> {{ localError }} </small>
    </span>
  </div>
</template>

<script lang="ts">
import { useFormattedDate } from '@/composable/useFormattedDate';
import { defineComponent, computed, watch, ref } from 'vue';

export default defineComponent({
  props: {
    label: {
      type: String,
      required: true,
    },
    placeholder: {
      type: String,
      default: null,
    },
    icon: {
      type: Boolean,
      default: true,
    },
    modelValue: {
    type: String,
    },
    error: {
      type: Array,
      default: () => []
    },
    readonly: {
      type: Boolean,
      default: false
    },
    isRequired: {
      type: Boolean,
      default: false
    },
    customClass: {
      type: String,
      default: '',
    },
    inputType: {
      type: String,
      default: 'date'
    },
    futureAllow: {
      type: Boolean,
      default: false
    },
    pastAllow: {
      type: Boolean,
      default: true
    },
    reset: {
      type: Boolean,
      default: false
    },
    hasChanges: {
      type: Boolean,
      default: false
    },
    errorPlacement: {
      type: String,
      default: 'bottom'
    },
  },
  setup(props, { emit }) {
    const currentType = computed(() => (props.readonly ? 'text' : props.inputType));
    const maxlength = computed(() => (props.placeholder ? (props.placeholder.length || 10) : 10));
    const inputRef = ref<HTMLInputElement | null>(null);
    const inputValue = ref(props.modelValue);
    const localError = ref<string|null>(null)
    const minDate = computed(() => !props.pastAllow ? new Date().toISOString().split('T')[0] : '1900-01-01');

    const maxDate = computed(() => {
      return props.futureAllow ? '' : new Date().toISOString().split('T')[0];
    });

    const pattern = computed(() => {
      if (props.placeholder === 'MM/YY') {
        return '^(0[1-9]|1[0-2])/\\d{2}$'
      }
      else if (props.placeholder === 'dd/mm/yyyy') {
        return '^(0[1-9]|[12][0-9]|3[01])/(0[1-9]|1[0-2])/\\d{4}$';
      }

      return '^(0[1-9]|1[0-9]|2[0-9]|3[0-1])/(0[1-9]|1[0-2])/\\d{4}$';
    });

    watch(() => props.modelValue, (newValue) => {
      inputValue.value = newValue;
    });

    watch(() => localError.value, (newValue) => {
      emit('update:error', newValue);
    });

    watch(() => props.reset, (newValue) => {
      if(newValue) {
        localError.value = null;
      }
    });

    const onInput = (event: Event) => {

      const target = event.target as HTMLInputElement;
      let value = target.value;
      const currentDate = new Date();
      currentDate.setHours(0, 0, 0, 0);
      const enteredDate = new Date(value);
      enteredDate.setHours(0, 0, 0, 0);
      const enteredYear = enteredDate.getFullYear();
      const enteredParts = value.split('-');
      const [year, month, day] = enteredParts.map(Number);

      if (year && year.toString().length > 4) {
        value = `${year.toString().slice(0, 4)}-${month || '01'}-${day || '01'}`;
        target.value = value;
        return;
      }

      if (enteredYear < 1900) {
        localError.value = 'Date must not be before the year 1900';
      } else if (enteredYear > 2200) {
        localError.value = 'Date must be before the year 2200';
      } else if (new Date(enteredDate).getTime() < new Date(currentDate).getTime() && !props.pastAllow) {
          localError.value = `Date must be today or a future date`;
      } else if (new Date(enteredDate).getTime() > new Date(currentDate).getTime() && !props.futureAllow) {
        localError.value = `Date must be today or a past date`;
      } else {
        localError.value = null;
      }

      if (!props.futureAllow && enteredDate > currentDate) {
          // Adjust the year to the current year if it's causing the future issue
          const adjustedYear = currentDate.getFullYear();

          // Construct a fallback date
          const validDate = new Date(adjustedYear, (month || 1) - 1, day || 1); // Fallback to a safe date

          const formattedDate = `${validDate.getFullYear()}-${String(validDate.getMonth() + 1).padStart(2, '0')}-${String(validDate.getDate()).padStart(2, '0')}`;

          inputValue.value = formattedDate;
          emit('update:modelValue', formattedDate);
          return; // Prevent further action
        }

      inputValue.value = value;
      emit('update:modelValue', value);
      emit('update:reset', false);
    };

    const focusInput = () => {
      if (inputRef.value && !props.readonly) {
        const inputElement = inputRef.value as HTMLInputElement & { showPicker: () => void };

        inputElement.focus();
        inputElement.showPicker();
      }
    };

    return {
      inputRef,
      localError,
      pattern,
      minDate,
      maxlength,
      maxDate,
      inputValue,
      currentType,
      onInput,
      focusInput
    };
  },
});
</script>
